import { Modal, Form, Row, Col, Radio, Popover } from 'antd';
import { ModalProps } from '@/types/ModalProps';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  Group,
  InputDecimal,
} from '@/components/_common';
import RadioGroup from '@/components/_common/RadioGroup';
import {
  useDefaultEmptyFieldError,
  useDefaultFormErrorNotification,
} from '@/hooks';
import {
  OperationType,
  UpdateAllTaskRequest,
} from '@/core/domain/operational/Tasks/Tasks.types';
import { useUpdateAllTask } from '@/core/domain/operational/Tasks/Tasks.hooks';
import { useIntl } from 'react-intl';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import { Info } from 'react-feather';

function ChangePriceModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl().formatMessage;

  const [form] = Form.useForm<UpdateAllTaskRequest>();

  const operation = Form.useWatch('operation', form);

  const percentOperation = operation === OperationType.PERCENT;
  const fixedPriceOperation = operation === OperationType.FIXED_PRICE;

  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const updateAllTask = useUpdateAllTask();

  const handleChangeAllPrice = async () => {
    try {
      const formvalues = await form.validateFields();

      const obj: UpdateAllTaskRequest = {
        operation,
        companyIds: formvalues.companyIds,
        price: formvalues?.price,
        pricePercent: formvalues.pricePercent
          ? formvalues.pricePercent / 100 + 1.0
          : undefined,
        subAreaId: formvalues?.subAreaId,
      };

      updateAllTask.mutateAsync(obj);

      handleVisible();
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  return (
    <Modal
      width={660}
      visible={visible}
      onCancel={handleVisible}
      onOk={() => {
        handleChangeAllPrice();
      }}
      title={intl({ id: 'task.changePrice.modal.title' })}
      destroyOnClose
    >
      <Form form={form} preserve={false}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <FloatingFormItem
              name="companyIds"
              label={intl({ id: 'task.changePrice.modal.company' })}
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField mode="multiple" />
            </FloatingFormItem>
          </Col>

          <Col
            span={24}
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FloatingFormItem
              name="subAreaId"
              label={intl({ id: 'task.changePrice.modal.areas' })}
              style={{ width: '100%' }}
            >
              <AreasInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              rules={[defaultEmptyFieldError]}
              name={percentOperation ? 'pricePercent' : 'price'}
              label={percentOperation ? 'Percentual de incidência' : 'Preço'}
            >
              <InputDecimal min={fixedPriceOperation ? 0 : undefined} />
            </FloatingFormItem>
          </Col>

          <Col
            span={24}
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FloatingFormItem
              name="operation"
              initialValue={OperationType.PRICE}
              getValueFromEvent={({ target: { value } }) => {
                form.resetFields(['price', 'pricePercent']);
                return value;
              }}
            >
              <RadioGroup>
                <Radio value={OperationType.PRICE}>
                  <Group gap={10}>
                    Somar valor
                    <Popover content="Soma o valor informado acima ao valor atual do serviço.">
                      <Info size={16} style={{ cursor: 'pointer' }} />
                    </Popover>
                  </Group>
                </Radio>

                <Radio value={OperationType.FIXED_PRICE}>
                  <Group gap={10}>
                    Substituir valor
                    <Popover content="Substitui o valor atual do serviço pelo valor informado acima.">
                      <Info size={16} style={{ cursor: 'pointer' }} />
                    </Popover>
                  </Group>
                </Radio>

                <Radio value={OperationType.PERCENT}>
                  <Group gap={10}>
                    Somar percentual
                    <Popover content="Soma o percentual informado acima ao valor atual do serviço.">
                      <Info size={16} style={{ cursor: 'pointer' }} />
                    </Popover>
                  </Group>
                </Radio>
              </RadioGroup>
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default ChangePriceModal;
